/**
 * Extracts a flat ordered list of leaf items from the contentDisplay tree (depth-first).
 */
export function walkLeaves(items) {
    const result = [];
    for (const item of items) {
        if (item.type === 'group') {
            result.push(...walkLeaves(item.children));
        }
        else {
            result.push({ id: item.id, visible: item.visible });
        }
    }
    return result;
}
/**
 * Returns options ordered by contentDisplay, with visibility applied.
 * Options not in contentDisplay are appended as non-visible.
 */
export function getSortedOptions({ options, contentDisplay, }) {
    const optionMap = new Map(options.map(o => [o.id, o]));
    const result = new Map();
    for (const { id, visible } of walkLeaves(contentDisplay)) {
        const option = optionMap.get(id);
        if (option) {
            result.set(id, { ...option, visible });
        }
    }
    for (const option of options) {
        if (!result.has(option.id)) {
            result.set(option.id, { ...option, visible: false });
        }
    }
    return Array.from(result.values());
}
/**
 * Converts contentDisplay tree into an internal OptionTreeNode tree,
 * resolving labels from options/groups definitions.
 */
export function buildOptionTree(options, groups, contentDisplay) {
    if (!groups.length) {
        const sorted = getSortedOptions({ options, contentDisplay });
        return sorted.map(opt => ({ ...opt, type: 'leaf' }));
    }
    const optionMap = new Map(options.map(o => [o.id, o]));
    const groupMap = new Map(groups.map(g => [g.id, g]));
    const convert = (items) => {
        var _a;
        const result = [];
        for (const item of items) {
            if (item.type === 'group') {
                const group = groupMap.get(item.id);
                result.push({
                    type: 'group',
                    id: item.id,
                    label: (_a = group === null || group === void 0 ? void 0 : group.label) !== null && _a !== void 0 ? _a : item.id,
                    visible: item.visible,
                    children: convert(item.children),
                });
            }
            else {
                const option = optionMap.get(item.id);
                if (option) {
                    result.push({ type: 'leaf', ...option, visible: item.visible });
                }
            }
        }
        return result;
    };
    return convert(contentDisplay);
}
/**
 * Converts OptionTreeNode[] back to ContentDisplayItem[].
 */
export function toContentDisplayItems(tree) {
    return tree.map(node => {
        if (node.type === 'group') {
            return {
                type: 'group',
                id: node.id,
                visible: node.visible,
                children: toContentDisplayItems(node.children),
            };
        }
        return { id: node.id, visible: node.visible };
    });
}
/**
 * Filters tree, keeping leaves matching filterText and groups with matching descendants.
 */
export function getFilteredTree(tree, filterText) {
    const text = filterText.trim().toLowerCase();
    if (!text) {
        return tree;
    }
    const result = [];
    for (const node of tree) {
        if (node.type === 'group') {
            const children = getFilteredTree(node.children, text);
            if (children.length > 0) {
                result.push({ ...node, children });
            }
        }
        else if (node.label.toLowerCase().includes(text)) {
            result.push(node);
        }
    }
    return result;
}
export function getFilteredOptions(options, filterText) {
    const text = filterText.trim().toLowerCase();
    if (!text) {
        return options;
    }
    return options.filter(option => option.label.toLowerCase().includes(text));
}
//# sourceMappingURL=utils.js.map